import { useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";

const COMPANY = {
  name: "EM GROUP",
  form: "SASU",
  capital: "100,00 €",
  hq: "60 Rue François Ier, 75008 Paris",
  siren: "100 603 026",
  siret: "100 603 026 00012",
  rcs: "100 603 026 R.C.S. Paris",
  vat: "FR89100603026",
  director: "Turpin Emerick",
  email: "contact@myflovea.com",
  website: "myflovea.com",
};

const HOST = {
  name: "Emergent Labs, Inc.",
  address: "Emergent Labs, Inc. — San Francisco, California, États-Unis",
  contact: "Site web : https://emergent.sh — Contact : hello@emergent.sh",
};

const MEDIATOR = {
  name: "CNPM Médiation Consommation",
  address: "27 avenue de la Libération, 42400 Saint-Chamond, France",
  website: "https://www.cnpm-mediation-consommation.eu",
  email: "contact-admin@cnpm-mediation-consommation.eu",
};

const ODR_URL = "https://consumer-redress.ec.europa.eu/dispute-resolution-bodies_en";

const LEGAL_META = {
  notice: {
    fr: { title: "Mentions légales", desc: `Mentions légales de ${COMPANY.name} SASU — éditeur du site myflovea.com, siège social, RCS, TVA, hébergeur et directeur de publication.` },
    en: { title: "Legal notice", desc: `Legal notice of ${COMPANY.name} SASU — publisher of myflovea.com, head office, RCS, VAT, hosting provider and publication director.` },
  },
  cgv: {
    fr: { title: "CGV — Conditions générales de vente", desc: "Conditions générales de vente myFLOVEA : prix TTC, livraison Europe gratuite, paiement sécurisé Stripe, droit de rétractation 14 jours, garantie 2 ans, médiation." },
    en: { title: "Terms of sale", desc: "myFLOVEA terms of sale: prices incl. VAT, free European delivery, secure Stripe payment, 14-day withdrawal right, 2-year warranty, mediation." },
  },
  privacy: {
    fr: { title: "Politique de confidentialité", desc: "Politique de confidentialité myFLOVEA conforme RGPD : données collectées, durée de conservation, droits utilisateurs, sous-traitants Stripe / Resend / Emergent." },
    en: { title: "Privacy policy", desc: "myFLOVEA GDPR-compliant privacy policy: data collected, retention, user rights, Stripe / Resend / Emergent sub-processors." },
  },
  cookies: {
    fr: { title: "Politique cookies", desc: "Politique cookies myFLOVEA conforme CNIL : cookies nécessaires, analytics, marketing, durée 13 mois, consentement et désinscription en 1 clic." },
    en: { title: "Cookie policy", desc: "myFLOVEA CNIL-compliant cookie policy: necessary, analytics, marketing cookies, 13-month retention, consent and 1-click opt-out." },
  },
  returns: {
    fr: { title: "Politique de retour 30 jours", desc: "Garantie commerciale myFLOVEA : satisfait ou remboursé 30 jours en plus du droit de rétractation légal de 14 jours." },
    en: { title: "30-day return policy", desc: "myFLOVEA commercial guarantee: 30-day money-back in addition to the legal 14-day right of withdrawal." },
  },
  retractation: {
    fr: { title: "Droit de rétractation", desc: "Politique de rétractation myFLOVEA conforme à la directive européenne 2023/2673 et à l'article L221-23 du Code de la consommation : délai 14 jours, modalités, conditions de retour, remboursement." },
    en: { title: "Right of withdrawal", desc: "myFLOVEA withdrawal policy compliant with EU directive 2023/2673 and article L221-23 of the French Consumer Code: 14-day period, procedure, return conditions, refund." },
  },
};

// ---------- CONTENT BUILDERS ----------
// Each page is a list of sections. A section has { heading?, paragraphs?, items? }.
// Rendered with proper typography below.

const noticePage = (isFr) => isFr
  ? [
      { paragraphs: [
        "Conformément à l'article 6-III de la Loi pour la Confiance dans l'Économie Numérique (LCEN) du 21 juin 2004, les utilisateurs du site myflovea.com sont informés de l'identité de l'éditeur et de l'hébergeur du site.",
      ]},
      { heading: "Éditeur du site", items: [
        `${COMPANY.name} — ${COMPANY.form} au capital de ${COMPANY.capital}`,
        `Siège social : ${COMPANY.hq}`,
        `SIREN : ${COMPANY.siren}`,
        `SIRET : ${COMPANY.siret}`,
        `RCS : ${COMPANY.rcs}`,
        `TVA intracommunautaire : ${COMPANY.vat}`,
        `Directeur de la publication : ${COMPANY.director}`,
        `Email : ${COMPANY.email}`,
        `Site web : ${COMPANY.website}`,
      ]},
      { heading: "Hébergeur", items: [
        HOST.name,
        HOST.address,
        HOST.contact,
      ]},
      { heading: "Propriété intellectuelle", paragraphs: [
        `Tous les contenus du site myflovea.com (textes, images, vidéos, logo, marque myFLOVEA) sont la propriété exclusive de ${COMPANY.name}. Toute reproduction totale ou partielle est interdite sans autorisation écrite préalable.`,
      ]},
      { heading: "Crédits photos", paragraphs: [
        `Photos : © myFLOVEA / ${COMPANY.name} — Tous droits réservés.`,
      ]},
    ]
  : [
      { paragraphs: [
        "In accordance with article 6-III of the French LCEN law (21 June 2004), users of myflovea.com are informed of the publisher and hosting provider of the website.",
      ]},
      { heading: "Website publisher", items: [
        `${COMPANY.name} — Simplified joint-stock company (SASU) with a capital of ${COMPANY.capital}`,
        `Registered office: ${COMPANY.hq}`,
        `SIREN: ${COMPANY.siren}`,
        `SIRET: ${COMPANY.siret}`,
        `Trade register: ${COMPANY.rcs}`,
        `Intra-EU VAT: ${COMPANY.vat}`,
        `Publication director: ${COMPANY.director}`,
        `Email: ${COMPANY.email}`,
        `Website: ${COMPANY.website}`,
      ]},
      { heading: "Hosting provider", items: [HOST.name, HOST.address, HOST.contact] },
      { heading: "Intellectual property", paragraphs: [
        `All content on myflovea.com (text, images, videos, logo, myFLOVEA trademark) is the exclusive property of ${COMPANY.name}. Any reproduction, in whole or in part, without prior written authorization is prohibited.`,
      ]},
      { heading: "Photo credits", paragraphs: [
        `Photos: © myFLOVEA / ${COMPANY.name} — All rights reserved.`,
      ]},
    ];

const cgvPage = (isFr) => isFr
  ? [
      { paragraphs: [
        `Les présentes Conditions Générales de Vente (CGV) régissent les ventes de produits effectuées par ${COMPANY.name} SASU via le site myflovea.com auprès de consommateurs particuliers (B2C). Toute commande implique l'acceptation pleine et entière des présentes CGV. Conformes aux articles L.441-1 et L.221-5 du Code de la consommation.`,
      ]},
      { heading: "Vendeur", items: [
        `${COMPANY.name} — ${COMPANY.form} au capital de ${COMPANY.capital}`,
        COMPANY.hq,
        `SIRET : ${COMPANY.siret} — ${COMPANY.rcs}`,
        `TVA : ${COMPANY.vat}`,
        `Email : ${COMPANY.email}`,
      ]},
      { heading: "Article 1 — Objet", paragraphs: [
        `Les présentes CGV ont pour objet de définir les droits et obligations des parties dans le cadre de la vente en ligne de produits myFLOVEA proposés par ${COMPANY.name} aux clients consommateurs.`,
      ]},
      { heading: "Article 2 — Produits", items: [
        "Coussin Massant Chauffant : 79€ TTC (Tissu Standard) / 89€ TTC (Velours Premium)",
        "La Sphère Duo — Massage Froid & Chaud : 23,90€ TTC (Sphère seule) / 29,90€ TTC (Ensemble complet)",
        "Produits certifiés CE conformes aux directives européennes en vigueur",
        "Caractéristiques essentielles décrites sur chaque fiche produit",
        "Photos à titre indicatif — légères variations possibles avec le produit réel",
      ]},
      { heading: "Article 3 — Prix", items: [
        "Prix affichés en euros TTC, TVA française 20% incluse",
        "Livraison gratuite en Europe",
        "Prix susceptibles d'être modifiés à tout moment",
        "Le prix applicable est celui affiché au moment de la validation de la commande",
      ]},
      { heading: "Article 4 — Commande", items: [
        "Récapitulatif complet affiché avant paiement",
        "Case à cocher obligatoire « J'accepte les CGV » avant validation",
        "Confirmation automatique par email après paiement",
        `${COMPANY.name} se réserve le droit de refuser toute commande anormale ou frauduleuse`,
        `Toute commande validée constitue un contrat de vente ferme entre ${COMPANY.name} et le client`,
      ]},
      { heading: "Article 5 — Paiement", items: [
        "Paiement sécurisé par carte bancaire via Stripe",
        "Cryptage SSL 100% sécurisé",
        "Paiement débité à la validation de la commande",
        "En cas de refus bancaire, la commande est automatiquement annulée",
        `${COMPANY.name} ne conserve aucune donnée bancaire`,
      ]},
      { heading: "Article 6 — Livraison", items: [
        "Coussin Massant Chauffant : expédié depuis notre entrepôt en Allemagne via DHL, 3 à 7 jours ouvrés — stock disponible en Europe",
        "La Sphère Duo : expédiée depuis l'entrepôt fournisseur, 8 à 14 jours ouvrés selon la destination",
        "Numéro de suivi envoyé par email automatiquement après expédition",
        "Livraison France et Union Européenne",
        "Délais indicatifs — non contractuels en cas de force majeure",
        "En cas de retard supérieur à 30 jours, le client peut annuler sa commande et obtenir remboursement intégral",
        `${COMPANY.name} ne peut être tenue responsable des retards imputables aux transporteurs`,
      ]},
      { heading: "Article 7 — Droit de rétractation (directive européenne 2023/2673 & article L.221-18 du Code de la consommation)", items: [
        "Délai : 14 jours calendaires à compter de la réception du produit, sans justification",
        `Modalités : envoyer un email à ${COMPANY.email} avec le numéro de commande et le formulaire ci-dessous`,
        "Remboursement intégral sous 14 jours après réception et vérification du retour",
        "Frais de retour à la charge du client",
        "Produit retourné dans son état d'origine, non utilisé, emballage intact",
        "En cas de dépréciation (produit endommagé, incomplet ou utilisé au-delà d'un simple essai), décote ou refus du remboursement possible — article L.221-23 alinéa 4",
        "Politique détaillée : voir la page dédiée « Politique de rétractation » (/legal/retractation)",
      ]},
      { heading: "Formulaire type de rétractation (article R.221-1 du Code de la consommation)", paragraphs: [
        `À l'attention de ${COMPANY.name} — myflovea.com — ${COMPANY.email}`,
        "Je notifie par la présente ma rétractation du contrat portant sur :",
      ], items: [
        "Produit : _______________",
        "Commandé le : _______________ — Reçu le : _______________",
        "Nom : _______________",
        "Adresse : _______________",
        "Date et signature : _______________",
      ]},
      { heading: "Article 8 — Garanties légales", items: [
        "Garantie légale de conformité : 2 ans à compter de la livraison (articles L.217-4 à L.217-14 du Code de la consommation)",
        "Garantie des vices cachés : 2 ans à compter de la découverte du vice (articles 1641 à 1649 du Code civil)",
        "Dans le cadre de la garantie légale, le client peut choisir entre réparation ou remplacement",
        "Garantie commerciale myFLOVEA : satisfait ou remboursé 30 jours sans condition",
      ]},
      { heading: "Article 9 — Service après-vente", items: [
        `Contact : ${COMPANY.email}`,
        "Réponse sous 24-48h ouvrées",
        "Produit défectueux : remplacement ou remboursement intégral au choix du client",
        "Photos du produit défectueux demandées pour traitement du dossier",
      ]},
      { heading: "Article 10 — Médiation de la consommation (article L.612-1 du Code de la consommation)", items: [
        "En cas de litige non résolu, le consommateur peut recourir gratuitement à la médiation",
        `Médiateur désigné : ${MEDIATOR.name}`,
        `Adresse : ${MEDIATOR.address}`,
        `Site : ${MEDIATOR.website}`,
        `Email : ${MEDIATOR.email}`,
        `Plateforme européenne de résolution des litiges de consommation : ${ODR_URL} (la plateforme ODR historique de la Commission européenne a été définitivement fermée le 20 juillet 2025 ; ce lien renvoie vers l'annuaire officiel des organes de règlement extrajudiciaire des litiges de l'Union européenne).`,
        `Le consommateur doit avoir tenté de résoudre le litige avec ${COMPANY.name} avant de saisir le médiateur`,
      ]},
      { heading: "Article 11 — Responsabilité", items: [
        `${COMPANY.name} ne peut être tenue responsable des dommages indirects`,
        "Produits certifiés CE conformes aux directives européennes",
        `${COMPANY.name} ne peut être tenue responsable d'un usage non conforme aux instructions`,
        `Force majeure : ${COMPANY.name} non responsable de tout événement hors de son contrôle`,
      ]},
      { heading: "Article 12 — Propriété intellectuelle", items: [
        `Tous les contenus du site myflovea.com sont la propriété exclusive de ${COMPANY.name}`,
        "Toute reproduction est interdite sans autorisation écrite préalable",
        "Marque myFLOVEA protégée — toute utilisation non autorisée sera poursuivie",
        `Photos : © myFLOVEA / ${COMPANY.name} — Tous droits réservés`,
      ]},
      { heading: "Article 13 — Données personnelles", paragraphs: [
        "Le traitement des données personnelles est conforme au Règlement Général sur la Protection des Données (RGPD). Voir la Politique de confidentialité.",
      ]},
      { heading: "Article 14 — Modification des CGV", items: [
        `${COMPANY.name} se réserve le droit de modifier les CGV à tout moment`,
        "Les CGV applicables sont celles en vigueur au moment de la commande",
      ]},
      { heading: "Article 15 — Droit applicable et juridiction", items: [
        "Droit français applicable",
        "Tribunaux compétents : Paris",
        "Pour les consommateurs européens hors France : la législation du pays de résidence peut s'appliquer",
      ]},
    ]
  : [
      { paragraphs: [
        `These Terms of Sale govern sales of products made by ${COMPANY.name} SASU through myflovea.com to individual consumers (B2C). Any order implies full acceptance of these Terms.`,
      ]},
      { heading: "Seller", items: [
        `${COMPANY.name} — Simplified joint-stock company (SASU) — capital ${COMPANY.capital}`,
        COMPANY.hq,
        `SIRET: ${COMPANY.siret} — RCS Paris`,
        `VAT: ${COMPANY.vat}`,
        `Email: ${COMPANY.email}`,
      ]},
      { heading: "Article 1 — Object", paragraphs: [
        `These Terms define the rights and obligations of the parties in the context of online sales of myFLOVEA products offered by ${COMPANY.name} to consumer customers.`,
      ]},
      { heading: "Article 2 — Products", items: [
        "Heated Massage Cushion: €79 incl. VAT (Standard Fabric) / €89 incl. VAT (Premium Velvet)",
        "The Duo Sphere — Cold & Hot Massage: €23.90 incl. VAT (Sphere only) / €29.90 incl. VAT (Complete set)",
        "Products are CE certified in accordance with applicable European directives",
        "Essential features are described on each product page",
        "Photos are illustrative — slight variations possible with the real product",
      ]},
      { heading: "Article 3 — Prices", items: [
        "Prices in euros incl. 20% French VAT",
        "Free delivery in Europe",
        "Prices may be updated at any time",
        "The applicable price is the one displayed at order validation",
      ]},
      { heading: "Article 4 — Order", items: [
        "Full summary shown before payment",
        "Mandatory \"I accept the Terms\" checkbox before validation",
        "Automatic email confirmation after payment",
        `${COMPANY.name} reserves the right to refuse any abnormal or fraudulent order`,
        `Any validated order constitutes a firm sales contract between ${COMPANY.name} and the customer`,
      ]},
      { heading: "Article 5 — Payment", items: [
        "Secure payment by card via Stripe",
        "100% secure SSL encryption",
        "Payment charged at order validation",
        "Card refusal automatically cancels the order",
        `${COMPANY.name} stores no bank card data`,
      ]},
      { heading: "Article 6 — Delivery", items: [
        "Heated Massage Cushion: from our Germany warehouse via DHL, 3-7 business days — stock available in Europe",
        "The Duo Sphere: from supplier warehouse, 8-14 business days depending on destination",
        "Automatic tracking number by email after shipment",
        "France and European Union",
        "Indicative timing — not contractual in case of force majeure",
        "If delivery is delayed beyond 30 days, the customer can cancel the order and get a full refund",
        `${COMPANY.name} cannot be held responsible for delays caused by carriers`,
      ]},
      { heading: "Article 7 — Right of withdrawal (article L.221-18 of the French Consumer Code)", items: [
        "Period: 14 calendar days from receipt of the product, without justification",
        `Modalities: send an email to ${COMPANY.email} with the order number and the form below`,
        "Full refund within 14 days after reception and verification of the return",
        "Return shipping fees borne by the customer",
        "Product returned in its original condition, unused, with intact packaging",
        "The right of withdrawal does not apply to products unsealed for hygiene reasons",
      ]},
      { heading: "Withdrawal form template", paragraphs: [
        `To the attention of ${COMPANY.name} — myflovea.com — ${COMPANY.email}`,
        "I hereby notify my withdrawal from the contract for:",
      ], items: [
        "Product: _______________",
        "Ordered on: _______________ — Received on: _______________",
        "Name: _______________",
        "Address: _______________",
        "Date and signature: _______________",
      ]},
      { heading: "Article 8 — Legal warranties", items: [
        "Legal warranty of conformity: 2 years from delivery (articles L.217-4 to L.217-14 of the French Consumer Code)",
        "Hidden defects warranty: 2 years from discovery (articles 1641 to 1649 of the French Civil Code)",
        "Under legal warranty, the customer may choose between repair or replacement",
        "myFLOVEA commercial guarantee: 30-day satisfied-or-refunded, no conditions",
      ]},
      { heading: "Article 9 — After-sales service", items: [
        `Contact: ${COMPANY.email}`,
        "Reply within 24-48 business hours",
        "Defective product: replacement or full refund at the customer's choice",
        "Photos of the defective product required for case processing",
      ]},
      { heading: "Article 10 — Consumer mediation", items: [
        "In case of unresolved dispute, the consumer can use mediation free of charge",
        `Designated mediator: ${MEDIATOR.name}`,
        `Address: ${MEDIATOR.address}`,
        `Website: ${MEDIATOR.website}`,
        `Email: ${MEDIATOR.email}`,
        `European consumer dispute resolution portal: ${ODR_URL} (the historical EU ODR platform was permanently closed on 20 July 2025; this link points to the official EU directory of alternative dispute resolution bodies).`,
        `The consumer must have first attempted to resolve the dispute with ${COMPANY.name} before referring to the mediator`,
      ]},
      { heading: "Article 11 — Liability", items: [
        `${COMPANY.name} cannot be held responsible for indirect damages`,
        "Products are CE certified",
        `${COMPANY.name} is not responsible for use inconsistent with instructions`,
        `Force majeure: ${COMPANY.name} not responsible for any event outside its control`,
      ]},
      { heading: "Article 12 — Intellectual property", items: [
        `All myflovea.com content is the exclusive property of ${COMPANY.name}`,
        "Reproduction is prohibited without prior written authorization",
        "myFLOVEA trademark is protected — any unauthorized use will be prosecuted",
        `Photos: © myFLOVEA / ${COMPANY.name} — All rights reserved`,
      ]},
      { heading: "Article 13 — Personal data", paragraphs: [
        "Personal data processing complies with the GDPR. See the Privacy Policy.",
      ]},
      { heading: "Article 14 — Terms changes", items: [
        `${COMPANY.name} reserves the right to modify the Terms at any time`,
        "Applicable Terms are those in force at the time of the order",
      ]},
      { heading: "Article 15 — Applicable law and jurisdiction", items: [
        "French law applies",
        "Competent courts: Paris",
        "For EU consumers outside France: legislation of the country of residence may apply",
      ]},
    ];

const privacyPage = (isFr) => isFr
  ? [
      { paragraphs: [
        "La présente politique de confidentialité est conforme au Règlement (UE) 2016/679 du 27 avril 2016 (Règlement Général sur la Protection des Données — RGPD) et à la loi française « Informatique et Libertés ».",
      ]},
      { heading: "Responsable du traitement", items: [
        `${COMPANY.name} SASU, ${COMPANY.hq}`,
        `Directeur de la publication : ${COMPANY.director}`,
        `Contact DPO : ${COMPANY.email}`,
      ]},
      { heading: "Données collectées", items: [
        "Identité : nom, prénom",
        "Coordonnées : email, adresse postale, pays",
        "Données de paiement : traitées directement par Stripe — aucune donnée bancaire stockée par EM GROUP",
        "Données de navigation : adresse IP, cookies (voir Politique cookies)",
      ]},
      { heading: "Finalités du traitement", items: [
        "Traitement et expédition des commandes",
        "Suivi de la livraison et service après-vente",
        "Envoi d'emails marketing (uniquement avec consentement explicite)",
        "Conformité aux obligations légales (facturation, comptabilité)",
      ]},
      { heading: "Base légale", items: [
        "Exécution du contrat de vente (commandes, livraison, SAV)",
        "Consentement explicite (newsletter, cookies non essentiels)",
        "Obligations légales (facturation, comptabilité)",
        "Intérêt légitime (sécurité du site, prévention de la fraude)",
      ]},
      { heading: "Durée de conservation", items: [
        "Données de commande : 10 ans (obligations comptables et fiscales)",
        "Données de compte et marketing : 3 ans après dernière commande ou dernier contact",
        "Cookies : maximum 13 mois",
      ]},
      { heading: "Vos droits", paragraphs: [
        `Conformément au RGPD, vous disposez à tout moment des droits suivants : droit d'accès, de rectification, d'effacement (« droit à l'oubli »), de limitation du traitement, de portabilité, et d'opposition. Pour exercer ces droits, écrivez à ${COMPANY.email}. Une réponse vous sera apportée dans un délai maximum de 30 jours.`,
      ]},
      { heading: "Sous-traitants", items: [
        "Stripe (Stripe Payments Europe, Ltd. — Irlande) : traitement des paiements",
        "Resend (Resend, Inc. — USA) : envoi d'emails transactionnels et marketing",
        "Emergent : hébergement du site web",
        "Tous les sous-traitants sont engagés contractuellement à respecter le RGPD",
      ]},
      { heading: "Sécurité", paragraphs: [
        "Vos données sont stockées sur des serveurs sécurisés (chiffrement SSL/TLS en transit et au repos). Les accès sont strictement limités au personnel habilité.",
      ]},
      { heading: "Réclamation", paragraphs: [
        "Vous avez le droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) — 3 Place de Fontenoy, 75007 Paris — www.cnil.fr",
      ]},
    ]
  : [
      { paragraphs: ["This privacy policy complies with the EU GDPR (Regulation 2016/679 of 27 April 2016)."] },
      { heading: "Data controller", items: [
        `${COMPANY.name} SASU, ${COMPANY.hq}`,
        `Publication director: ${COMPANY.director}`,
        `DPO contact: ${COMPANY.email}`,
      ]},
      { heading: "Data collected", items: [
        "Identity: first name, last name",
        "Contact details: email, postal address, country",
        "Payment data: processed directly by Stripe — no bank data stored by EM GROUP",
        "Browsing data: IP address, cookies (see Cookie Policy)",
      ]},
      { heading: "Purposes", items: [
        "Order processing and shipping",
        "Delivery tracking and after-sales service",
        "Marketing emails (with explicit consent only)",
        "Legal compliance (invoicing, accounting)",
      ]},
      { heading: "Legal basis", items: [
        "Contract execution (orders, delivery, after-sales)",
        "Explicit consent (newsletter, non-essential cookies)",
        "Legal obligations (invoicing, accounting)",
        "Legitimate interest (site security, fraud prevention)",
      ]},
      { heading: "Retention", items: [
        "Order data: 10 years (accounting and tax obligations)",
        "Account and marketing data: 3 years after last order or contact",
        "Cookies: maximum 13 months",
      ]},
      { heading: "Your rights", paragraphs: [
        `Under the GDPR you have the following rights at any time: access, rectification, erasure (right to be forgotten), restriction of processing, portability, and objection. Write to ${COMPANY.email} to exercise them. We will respond within a maximum of 30 days.`,
      ]},
      { heading: "Sub-processors", items: [
        "Stripe (Stripe Payments Europe Ltd. — Ireland): payment processing",
        "Resend (Resend, Inc. — USA): transactional and marketing email delivery",
        "Emergent: website hosting",
        "All sub-processors are contractually committed to GDPR compliance",
      ]},
      { heading: "Security", paragraphs: [
        "Your data is stored on secure servers (SSL/TLS encryption in transit and at rest). Access is strictly limited to authorized personnel.",
      ]},
      { heading: "Complaints", paragraphs: [
        "You have the right to file a complaint with the French CNIL — 3 Place de Fontenoy, 75007 Paris — www.cnil.fr",
      ]},
    ];

const cookiesPage = (isFr) => isFr
  ? [
      { paragraphs: [
        "La présente politique cookies est conforme aux recommandations de la CNIL et au Règlement ePrivacy. Lors de votre première visite, un bandeau d'information vous permet d'accepter ou de refuser les cookies non essentiels.",
      ]},
      { heading: "Qu'est-ce qu'un cookie ?", paragraphs: [
        "Un cookie est un petit fichier texte déposé sur votre terminal lors de la consultation d'un site web. Il permet au site de mémoriser vos préférences ou de mesurer son audience.",
      ]},
      { heading: "Cookies utilisés sur myflovea.com", items: [
        "Cookies nécessaires (session, panier, sécurité, langue) — exemptés de consentement",
        "Cookies analytics : mesure d'audience anonyme — consentement requis",
        "Cookies marketing : retargeting publicitaire — consentement requis",
      ]},
      { heading: "Votre consentement", items: [
        "Bandeau cookies présenté au premier chargement avec choix clair",
        "Boutons « Tout accepter » et « Tout refuser » de même taille et même importance visuelle (conformité CNIL)",
        "Bouton « Personnaliser » permettant un choix granulaire par catégorie",
        "Votre choix est mémorisé pendant 6 mois — vous pouvez le modifier à tout moment",
      ]},
      { heading: "Durée de conservation", paragraphs: [
        "Conformément aux recommandations CNIL, les cookies ont une durée de vie maximale de 13 mois.",
      ]},
      { heading: "Comment modifier vos choix ?", paragraphs: [
        "Cliquez sur le lien « Gérer mes cookies » présent en bas de chaque page du site. Vous pouvez également configurer votre navigateur pour bloquer tous les cookies (Chrome, Firefox, Safari, Edge — voir l'aide de votre navigateur).",
      ]},
    ]
  : [
      { paragraphs: [
        "This cookie policy complies with French CNIL recommendations and the EU ePrivacy regulation. On your first visit, an information banner lets you accept or refuse non-essential cookies.",
      ]},
      { heading: "What is a cookie?", paragraphs: [
        "A cookie is a small text file stored on your device when you visit a website. It allows the site to remember your preferences or measure audience.",
      ]},
      { heading: "Cookies used on myflovea.com", items: [
        "Necessary cookies (session, cart, security, language) — exempt from consent",
        "Analytics cookies: anonymous audience measurement — consent required",
        "Marketing cookies: ad retargeting — consent required",
      ]},
      { heading: "Your consent", items: [
        "Cookie banner shown on first load with clear choice",
        "\"Accept all\" and \"Reject all\" buttons of equal size and visual prominence (CNIL compliance)",
        "\"Customize\" button for granular per-category choice",
        "Your choice is stored for 6 months — you can change it at any time",
      ]},
      { heading: "Retention", paragraphs: [
        "Following CNIL guidance, cookies have a maximum lifetime of 13 months.",
      ]},
      { heading: "How to change your choices", paragraphs: [
        "Click the \"Manage my cookies\" link at the bottom of every page. You can also configure your browser to block all cookies (Chrome, Firefox, Safari, Edge — see your browser's help).",
      ]},
    ];

const returnsPage = (isFr) => isFr
  ? [
      { paragraphs: [
        `Dernière mise à jour : ${new Date().getFullYear()}`,
        `En complément du droit de rétractation légal de 14 jours, ${COMPANY.brand} offre une garantie commerciale « satisfait ou remboursé » de 30 jours calendaires à compter de la réception de votre commande.`,
        `Cette garantie commerciale est offerte à la discrétion d'${COMPANY.name} et peut être refusée ou ajustée si les conditions ci-dessous ne sont pas respectées.`,
      ]},
      { heading: "Modalités", items: [
        `Contactez-nous à ${COMPANY.email} avec votre numéro de commande et le motif de votre demande`,
        "Merci de joindre des photos du produit et de son emballage, permettant de vérifier son état",
        "Après validation, nous vous envoyons un bordereau de retour prépayé",
        "Remboursement intégral sous 14 jours après réception et vérification du produit",
      ]},
      { heading: "Conditions de la garantie commerciale", items: [
        "✅ Produit non utilisé, en parfait état de revente",
        "✅ Emballage d'origine intact",
        "✅ Tous les accessoires inclus",
      ]},
      { heading: "⚠️ Décote ou refus possible", paragraphs: [
        `Si le produit ou son emballage présente des signes d'usage, de détérioration ou de manipulation excessive, ${COMPANY.name} se réserve le droit :`,
      ], items: [
        "d'appliquer une décote proportionnelle sur le montant remboursé, ou",
        "de refuser le bénéfice de cette garantie commerciale (sans que cela affecte vos droits légaux au titre du droit de rétractation de 14 jours ou des garanties légales ci-dessous), si l'état du produit ne permet manifestement pas sa remise en vente.",
      ]},
      { paragraphs: [
        `Cette garantie commerciale de 30 jours est une offre supplémentaire et volontaire d'${COMPANY.name}, distincte du droit de rétractation légal de 14 jours, et n'affecte en rien vos droits légaux.`,
      ]},
      { heading: "Frais de retour", paragraphs: [
        "Un bordereau de retour prépayé vous est fourni dans le cadre de cette garantie commerciale de 30 jours.",
      ]},
      { heading: "Remboursement", paragraphs: [
        `Le remboursement (déduction faite d'une éventuelle décote) sera effectué sous 14 jours après réception et vérification du produit retourné, via le moyen de paiement utilisé lors de la commande (Stripe).`,
      ]},
      { heading: "Garanties légales — rappel", items: [
        "Garantie de conformité : 2 ans (article L.217-4 du Code de la consommation)",
        "Garantie des vices cachés : 2 ans (article 1641 du Code civil)",
      ]},
      { paragraphs: [
        "Ces garanties légales s'appliquent indépendamment de la présente garantie commerciale et ne peuvent être ni réduites ni supprimées.",
      ]},
    ]
  : [
      { paragraphs: [
        `Last updated: ${new Date().getFullYear()}`,
        `In addition to the legal 14-day right of withdrawal, ${COMPANY.brand} offers a 30-day satisfied-or-refunded commercial guarantee, running from the date of receipt of your order.`,
        `This commercial guarantee is offered at ${COMPANY.name}'s discretion and may be refused or adjusted if the conditions below are not met.`,
      ]},
      { heading: "How to proceed", items: [
        `Contact us at ${COMPANY.email} with your order number and the reason for your request`,
        "Please attach photos of the product and its packaging so we can verify its condition",
        "After validation, we send you a prepaid return label",
        "Full refund within 14 days after receipt and verification of the product",
      ]},
      { heading: "Commercial guarantee conditions", items: [
        "✅ Product unused, in perfect resale condition",
        "✅ Original packaging intact",
        "✅ All accessories included",
      ]},
      { heading: "⚠️ Depreciation or refusal possible", paragraphs: [
        `If the product or its packaging shows signs of use, deterioration or excessive handling, ${COMPANY.name} reserves the right to:`,
      ], items: [
        "apply a proportional deduction on the refunded amount, or",
        "refuse this commercial guarantee (without affecting your statutory rights under the 14-day right of withdrawal or the legal warranties below), if the product's condition clearly does not allow resale.",
      ]},
      { paragraphs: [
        `This 30-day commercial guarantee is an additional, voluntary offer from ${COMPANY.name}, distinct from the legal 14-day right of withdrawal, and does not affect your statutory rights.`,
      ]},
      { heading: "Return shipping fees", paragraphs: [
        "A prepaid return label is provided within this 30-day commercial guarantee.",
      ]},
      { heading: "Refund", paragraphs: [
        `The refund (minus any observed depreciation) will be issued within 14 days after receipt and verification of the returned product, via the payment method used at checkout (Stripe).`,
      ]},
      { heading: "Legal warranties — reminder", items: [
        "Conformity warranty: 2 years (article L.217-4 of the French Consumer Code)",
        "Hidden defects warranty: 2 years (article 1641 of the French Civil Code)",
      ]},
      { paragraphs: [
        "These legal warranties apply independently of this commercial guarantee and cannot be reduced or removed.",
      ]},
    ];

const retractationPage = (isFr) => isFr
  ? [
      { paragraphs: [
        `Dernière mise à jour : ${new Date().getFullYear()}`,
        `Conformément à l'article L.221-18 du Code de la consommation, vous disposez d'un délai de 14 jours à compter de la réception de votre commande pour exercer votre droit de rétractation, sans avoir à justifier de motif ni à payer de pénalité, à l'exception des frais de retour.`,
      ]},
      { heading: "Comment exercer votre droit ?", paragraphs: [
        `Contactez-nous par email à ${COMPANY.email} en indiquant votre numéro de commande et votre demande de rétractation. Vous pouvez également utiliser le formulaire type prévu à l'article R.221-1 du Code de la consommation, disponible dans nos CGV.`,
        `Vous disposez ensuite de 14 jours supplémentaires à compter de votre déclaration pour nous retourner le produit.`,
      ]},
      { heading: "Conditions de retour", items: [
        "✅ Dans son emballage d'origine, ou un emballage offrant une protection équivalente",
        "✅ Avec tous les accessoires inclus (télécommande, câble USB, notice, etc.)",
        "✅ Accompagné de photos du produit et de l'emballage à l'envoi de votre demande",
      ]},
      { paragraphs: [
        `Le droit de rétractation vous permet de manipuler et tester le produit comme vous le feriez en magasin, avant de décider de le conserver. Une manipulation excédant ce qui est nécessaire pour vérifier la nature, les caractéristiques et le bon fonctionnement du produit peut entraîner une dépréciation de sa valeur.`,
      ]},
      { heading: "Dépréciation du produit (article L.221-23 du Code de la consommation)", paragraphs: [
        `Si le produit présente des signes d'usage, de détérioration ou un emballage endommagé au-delà d'une simple vérification, ${COMPANY.name} se réserve le droit d'appliquer une déduction proportionnelle à cette dépréciation sur le montant remboursé. Cette déduction est calculée au cas par cas selon l'état constaté du produit retourné.`,
      ]},
      { heading: "Frais de retour", paragraphs: [
        `Les frais de retour sont à la charge du client, sauf dans le cadre de notre garantie commerciale de 30 jours (voir ci-dessous), où un bordereau prépayé est fourni.`,
      ]},
      { heading: "Remboursement", paragraphs: [
        `Le remboursement (déduction faite d'une éventuelle dépréciation constatée) sera effectué dans un délai maximum de 14 jours après réception et vérification du produit retourné, via le moyen de paiement utilisé lors de la commande.`,
      ]},
      { heading: "Garantie commerciale — 30 jours en plus", paragraphs: [
        `En complément de ce droit légal, ${COMPANY.name} propose une garantie commerciale « satisfait ou remboursé » de 30 jours calendaires. Voir la page dédiée : /legal/returns.`,
      ]},
      { heading: "Contact", paragraphs: [
        `Email : ${COMPANY.email}`,
        `Réponse sous 24 à 48 heures ouvrées.`,
      ]},
    ]
  : [
      { paragraphs: [
        `Last updated: ${new Date().getFullYear()}`,
        `In accordance with article L.221-18 of the French Consumer Code, you have 14 days from the receipt of your order to exercise your right of withdrawal, without justification or penalty, except for return shipping costs.`,
      ]},
      { heading: "How to exercise your right?", paragraphs: [
        `Contact us by email at ${COMPANY.email}, indicating your order number and your withdrawal request. You may also use the standard form provided by article R.221-1 of the French Consumer Code, available in our Terms of Sale.`,
        `You then have an additional 14 days from your declaration to return the product to us.`,
      ]},
      { heading: "Return conditions", items: [
        "✅ In its original packaging, or packaging offering equivalent protection",
        "✅ With all included accessories (remote control, USB cable, manual, etc.)",
        "✅ Accompanied by photos of the product and packaging when you send your request",
      ]},
      { paragraphs: [
        `The right of withdrawal lets you handle and test the product as you would in a store, before deciding to keep it. Handling beyond what is necessary to verify the nature, characteristics and proper functioning of the product may result in a depreciation of its value.`,
      ]},
      { heading: "Product depreciation (article L.221-23 of the French Consumer Code)", paragraphs: [
        `If the product shows signs of use, deterioration or damaged packaging beyond a simple inspection, ${COMPANY.name} reserves the right to apply a deduction proportional to this depreciation on the refunded amount. This deduction is calculated on a case-by-case basis according to the observed condition of the returned product.`,
      ]},
      { heading: "Return shipping fees", paragraphs: [
        `Return shipping fees are borne by the customer, except within our 30-day commercial guarantee (see below), where a prepaid label is provided.`,
      ]},
      { heading: "Refund", paragraphs: [
        `The refund (minus any observed depreciation) will be issued within a maximum of 14 days after receipt and verification of the returned product, via the payment method used at checkout.`,
      ]},
      { heading: "Commercial guarantee — extra 30 days", paragraphs: [
        `In addition to this statutory right, ${COMPANY.name} offers a 30-day money-back commercial guarantee. See the dedicated page: /legal/returns.`,
      ]},
      { heading: "Contact", paragraphs: [
        `Email: ${COMPANY.email}`,
        `Reply within 24 to 48 business hours.`,
      ]},
    ];

const LEGAL_CONTENT = {
  notice: { build: noticePage },
  cgv: { build: cgvPage },
  privacy: { build: privacyPage },
  cookies: { build: cookiesPage },
  returns: { build: returnsPage },
  retractation: { build: retractationPage },
};

const Legal = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const isFr = lang === "fr";
  const data = LEGAL_CONTENT[slug];
  if (!data) {
    return (
      <section data-testid={`legal-${slug}`} className="max-w-3xl mx-auto px-6 sm:px-10 py-20 md:py-28">
        <h1 className="font-serif-display text-3xl md:text-4xl">
          {isFr ? "Page introuvable" : "Page not found"}
        </h1>
      </section>
    );
  }
  const sections = data.build(isFr);
  const meta = (LEGAL_META[slug] && LEGAL_META[slug][lang]) || null;
  const title = meta ? meta.title : "";

  return (
    <section data-testid={`legal-${slug}`} className="max-w-3xl mx-auto px-6 sm:px-10 py-16 md:py-24">
      {meta && (
        <Seo lang={lang} path={`/legal/${slug}`} title={meta.title} description={meta.desc} />
      )}
      <div className="eyebrow mb-6">myFLOVEA</div>
      <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
        {title}
      </h1>
      <div className="text-xs text-[#8a8378] mt-3">
        {isFr ? "Dernière mise à jour : " : "Last updated: "}
        {new Date().getFullYear()}
      </div>

      <div className="mt-12 space-y-12">
        {sections.map((s, i) => (
          <div key={i}>
            {s.heading && (
              <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-4 leading-tight">
                {s.heading}
              </h2>
            )}
            {s.paragraphs?.map((p, j) => (
              <p key={`p-${j}`} className="text-[15px] leading-relaxed text-[#3D2B1F] mb-3">
                {p}
              </p>
            ))}
            {s.items && (
              <ul className="list-disc pl-5 mt-2 space-y-2 text-[15px] leading-relaxed text-[#3D2B1F]">
                {s.items.map((it, j) => <li key={`i-${j}`}>{it}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="mt-16 pt-8 border-t border-[#dcd7ce] text-xs text-[#8a8378]">
        © {new Date().getFullYear()} {COMPANY.name} — Tous droits réservés.
      </div>
    </section>
  );
};

export default Legal;
